import { getAppConfigSingleton } from "@expert/app-config";
import { useToast } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { type JSX, useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { shallow } from "zustand/shallow";
import type { Customer } from "@/customer-info";
import { useVerifiedCustomerStore } from "@/customer-info";
import { updateCase } from "@/customer-info/api/updateCase";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { isVoiceTask, useAgentSdk, useAgentStore, useCallbackState, usePartner, useSession } from "@/sdk";
import { getBackupGaiaSummary, getGaiaSummary } from "../api/sessionSummaryApiClient";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { EditSessionSummaryModal } from "./EditSessionSummaryModal";
import styles from "./EditSessionSummaryModal.module.css";
import { adaptSimplifiedSummary } from "./utils";

const logger = getLogger({ module: "SessionSummary", supportTeam: "solve" });

/**
 * Stable empty customer object reference to prevent infinite loops from new references when using store selectors.
 */
const EMPTY_CUSTOMER: Partial<Customer> = {};

// TODO: Rename this component because it's not deprecated, and actually used by every partner except Verizon
export function DeprecatedSessionSummary(): JSX.Element | null {
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();

    const { accountNumber, metadata: { caseNumber, caseId } = {} } = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(activeSession.id).customer ?? EMPTY_CUSTOMER,
    );
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const { accountNumberLength, wrappingAgentActivity, gaiaSummaryModel } = appConfig.getPartnersConfig(partner);
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const {
        features: { isAutoSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const validAccountNumber = simplifiedSummary
        ? !!caseId
        : accountNumberLength
          ? accountNumber?.length === accountNumberLength
          : false;

    const [setCanAppend, setSubmitted, autogeneratedSummary] = useSessionSummaryStore((state) => [
        state.setCanAppend,
        state.setSubmitted,
        state.autogeneratedSummary,
    ]);

    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);

    const toast = useToast();
    const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);

    const agentActivity = useAgentStore(useShallow((state) => state.activity));
    const callbackState = useCallbackState();

    const {
        summary,
        submitted,
        setSessionSummary,
        setSubject,
        subject,
        category,
        subcategory,
        setCategory,
        setSubcategory,
    } = useSessionSummaryStore((state) => state, shallow);
    const activeTask = activeSession.currentTask;

    const agentCallSid =
        activeTask && isVoiceTask(activeTask)
            ? (activeTask.agentCallId ??
              Array.from(activeSession.tasks.values())
                  .filter(isVoiceTask)
                  .findLast((task) => task.agentCallId !== undefined)?.agentCallId)
            : undefined;

    const shouldAutoGenerateSummary = activeTask?.locale !== "es-mx" && !!gaiaSummaryModel;

    // A wrapping task or a newly wrapping agent means we are entering session summary
    const inWrappingState = activeTask?.status === "wrapping" || agentActivity === wrappingAgentActivity;

    const taskIsActive = activeTask?.status === "assigned";
    const hasImmediateCallBack = callbackState?.callbackType === "CallbackNow";

    const handleSimplifiedEndSession = async () => {
        try {
            if (!caseId) {
                await navigator.clipboard.writeText(summary);
                toast.success("Summary copied to clipboard");
                return;
            }
            setSendingSummary(true);
            await updateCase({
                caseId,
                subject,
                description: summary,
                category,
                subcategory,
            });
            toast.success("Summary sent");
            setSubmitted(true);
        } catch (err) {
            logger.warn({ err }, "DeprecatedSessionSummary | Error sending summary");
            toast.error("Error sending summary. Please copy the summary manually.");
        } finally {
            setSendingSummary(false);
        }
    };

    const setSessionSummaryFromGaia = useCallback(async () => {
        logger.trace(
            { agentCallSid, sessionId: activeSession.id, partner },
            "DeprecatedSessionSummary | Gaia summary fetch check",
        );
        const generatedSummary: string = agentCallSid
            ? await getGaiaSummary({ callSid: agentCallSid, sessionId: activeSession.id, partner }, gaiaSummaryModel)
            : await getBackupGaiaSummary({ sessionId: activeSession.id, partner }, gaiaSummaryModel);

        if (!generatedSummary) {
            logger.warn("DeprecatedSessionSummary | Received empty summary from Gaia");
        } else if (simplifiedSummary) {
            const { subject, summary, category, subcategory } = adaptSimplifiedSummary(generatedSummary);
            setSessionSummary({ value: summary, autoGenerated: true });
            setSubject(subject);
            if (category) setCategory(category);
            if (subcategory) setSubcategory(subcategory);
        } else {
            setSessionSummary({ value: generatedSummary, autoGenerated: true });
        }
        setSummaryLoaded(true);
    }, [
        agentCallSid,
        activeSession.id,
        partner,
        setSessionSummary,
        setSummaryLoaded,
        gaiaSummaryModel,
        setSubject,
        simplifiedSummary,
        setSubcategory,
        setCategory,
    ]);

    useEffect(() => {
        if (!shouldAutoGenerateSummary) {
            setSummaryLoaded(true);
            return;
        }

        if (opened && inWrappingState && !summaryLoaded) {
            if (autogeneratedSummary && !submitted && simplifiedSummary) {
                setSummaryLoaded(true);
                if (caseId) {
                    void handleSimplifiedEndSession();
                }
            } else if (!autogeneratedSummary || !summaryLoaded) {
                // NOTE: The summary loaded check is to prevent getting stuck in the loading state if we've already generated a summary from a previous leg of the call
                void setSessionSummaryFromGaia();
            }
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [inWrappingState, shouldAutoGenerateSummary, opened, autogeneratedSummary, submitted, simplifiedSummary]);

    useEffect(() => {
        if (hasImmediateCallBack) return;

        if (inWrappingState) {
            openModal();
        } else if (taskIsActive) {
            setCanAppend(true);
        }
    }, [hasImmediateCallBack, inWrappingState, taskIsActive, setCanAppend, openModal]);

    const shouldSendSummary = !submitted && summary;

    async function handleWrappingExpired() {
        await handleEndSession("WrappingTimeExpired");
    }

    const handleCopyAndSendSummary = useCallback(async () => {
        async function writeToClipboard() {
            try {
                await navigator.clipboard.writeText(summary);
            } catch (err) {
                logger.warn({ err }, "DeprecatedSessionSummary | Error copying session summary");
            }
        }
        await Promise.all([writeToClipboard()]);
    }, [summary]);

    const handleEndSession = useCallback(
        async (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => {
            if (validAccountNumber && isAutoSummaryEnabled) {
                logger.trace(
                    { isAutoSummaryEnabled, partner, reason, validAccountNumber },
                    "DeprecatedSessionSummary | Ending session wrapup",
                );
            } else await handleCopyAndSendSummary();
            logger.trace({ reason, partner }, "DeprecatedSessionSummary | Ending session wrapup");
            if (activeTask) {
                await agentSdk.endWrapup(reason, partner, activeTask);
            }
        },
        [activeTask, agentSdk, handleCopyAndSendSummary, isAutoSummaryEnabled, partner, validAccountNumber],
    );

    const sendSummary = useCallback(async () => {
        toast.success("Session summary copied to clipboard");
        try {
            await navigator.clipboard.writeText(summary);
        } catch (err) {
            logger.warn({ err }, "DeprecatedSessionSummary | Error copying session summary");
        }
    }, [summary, toast]);

    const onDismiss = useCallback(
        async ({ checkSummary = true } = {}) => {
            logger.trace("DeprecatedSessionSummary | Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                await sendSummary();
            }
        },
        [closeModal, sendSummary, shouldSendSummary],
    );

    return (
        <Group className={styles.tempTimeline}>
            <EditSessionSummaryModal
                loading={!summaryLoaded && shouldAutoGenerateSummary}
                onDismiss={simplifiedSummary ? () => {} : () => void onDismiss()}
                sendingSummary={sendingSummary}
                onExpired={simplifiedSummary ? () => {} : handleWrappingExpired}
                simplifiedSummary={simplifiedSummary}
                caseNumber={caseNumber}
                validAccountNumber={validAccountNumber}
                endSession={simplifiedSummary ? handleSimplifiedEndSession : handleEndSession}
                opened={opened}
            />
        </Group>
    );
}
